import { cleApi } from "./client";
import type { PaginatedResponse } from "./courseCertificates";
import type {
  AddDocumentCommentRequest,
  DocumentCommentModel,
  DocumentReclassifyStatus,
  ReclassifyDocumentResponse,
} from "./models/documents";
import type {
  ReviewThreadListItemModel,
  ReviewSubjectType,
  ReviewThreadStatus,
} from "./models/reviewThreads";

const REVIEW_THREADS_BASE = "review/threads";
const REVIEW_SUBJECT_BASE = "review";

export interface GetReviewThreadsParams {
  status?: ReviewThreadStatus[];
  ownerUserId?: string;
  assignedAdminUserId?: string;
  /** Filtrér på en navngiven kollega. API'et udleder selv admin-id'et af e-mailen. */
  assignedAdminEmail?: string;
  subjectType?: ReviewSubjectType[];
  assignedTo?: boolean;
  page?: number;
  pageSize?: number;
}

export const getReviewThreads = ({
  status,
  ownerUserId,
  assignedAdminUserId,
  assignedAdminEmail,
  subjectType,
  assignedTo,
  page = 1,
  pageSize = 20,
}: GetReviewThreadsParams): Promise<
  PaginatedResponse<ReviewThreadListItemModel>
> => {
  const query = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });

  status?.forEach((value) => {
    query.append("status", value);
  });

  subjectType?.forEach((value) => {
    query.append("subjectType", value);
  });

  if (ownerUserId) {
    query.set("ownerUserId", ownerUserId);
  }

  if (assignedAdminUserId) {
    query.set("assignedAdminUserId", assignedAdminUserId);
  }

  if (assignedAdminEmail) {
    query.set("assignedAdminEmail", assignedAdminEmail);
  }

  if (assignedTo) {
    query.set("assignedTo", "true");
  }

  return cleApi.get<PaginatedResponse<ReviewThreadListItemModel>>(
    `${REVIEW_THREADS_BASE}?${query.toString()}`,
  );
};

export const getReviewThreadCommentsBySubjectId = (
  subjectId: string,
): Promise<DocumentCommentModel[]> =>
  cleApi.get<DocumentCommentModel[]>(
    `${REVIEW_SUBJECT_BASE}/${subjectId}/comments`,
  );

export const addReviewThreadCommentBySubjectId = (
  subjectId: string,
  { content }: AddDocumentCommentRequest,
): Promise<DocumentCommentModel> =>
  cleApi.post<DocumentCommentModel>(
    `${REVIEW_SUBJECT_BASE}/${subjectId}/comments`,
    { content },
  );

export const reclassifyReviewThreadBySubjectId = (
  subjectId: string,
  newStatus: DocumentReclassifyStatus,
): Promise<ReclassifyDocumentResponse> =>
  cleApi.post<ReclassifyDocumentResponse>(
    `${REVIEW_SUBJECT_BASE}/${subjectId}/reclassify`,
    { new_status: newStatus },
  );

export interface AssignReviewThreadRequest {
  /**
   * Den tildelte navngives ved e-mail, ikke ved id: Aika-brugerens id hører til en anden
   * database end CLE-API'ets admin-id'er, og kun API'et ved, hvordan sidstnævnte udledes.
   */
  assigneeEmail: string;
  adminDisplayName?: string;
}

export const assignReviewThread = (
  threadId: string,
  body: AssignReviewThreadRequest,
): Promise<ReviewThreadListItemModel> =>
  cleApi.post<ReviewThreadListItemModel>(
    `${REVIEW_THREADS_BASE}/${threadId}/assign`,
    body,
  );

export const assignReviewThreadToSelf = (
  threadId: string,
): Promise<ReviewThreadListItemModel> =>
  cleApi.post<ReviewThreadListItemModel>(
    `${REVIEW_THREADS_BASE}/${threadId}/assign-self`,
  );

export const markReviewThreadResolved = (
  threadId: string,
): Promise<ReviewThreadListItemModel> =>
  cleApi.post<ReviewThreadListItemModel>(
    `${REVIEW_THREADS_BASE}/${threadId}/resolved`,
  );
